/**
 * WC2026StatsPage — "Squads in Stats" editorial page
 * Route: /wc2026/stats
 *
 * Fullscreen vertical snap-scroll. One massive stat per slide.
 * Touch-optimised: swipe up/down to move between stats.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { wcImageUrl } from '../lib/assetUrls';
import { useKioskInactivity } from '../hooks/useKioskInactivity';

const API_URL = process.env.REACT_APP_BACKEND_URL;
function authH() { return { Authorization: `Bearer ${getToken()}` }; }

// ── Fallback stories — shown instantly while the API call resolves ────────────

const FALLBACK_STORIES = [
  { id: 1,  color: 'cyan',   category_label: 'Squads',    stat_number: '200',   stat_label: 'Players from England',        headline: 'Premier League dominates the squads',                body: 'A record 200 players based in the English top-flight have been named in WC2026 squads — more than any other league on the planet.',                                          image_key: 'wc-england'  },
  { id: 2,  color: 'amber',  category_label: 'Players',   stat_number: '43',    stat_label: 'Craig Gordon\'s age',          headline: 'Scotland\'s Gordon is the oldest player',            body: 'At 43, Scotland goalkeeper Craig Gordon is the oldest player at WC2026 — and the second-oldest ever to go to a World Cup, behind Egypt\'s Essam El Hadary (45 at Russia 2018). Cristiano Ronaldo (41) is fourth all-time, behind Roger Milla (42) and Faryd Mondragon (43). Ochoa, Modric, Dzeko, Neuer and Vozinha all follow at 40.',  image_key: 'wc-ronaldo'  },
  { id: 3,  color: 'green',  category_label: 'Host Bonus', stat_number: '25',   stat_label: 'Home-based players',           headline: 'Qatar & Saudi lean local',                           body: 'Between them, Qatar and Saudi Arabia have named 25 players from their own domestic leagues — a big vote of confidence in Gulf football development.',                       image_key: 'wc-gulf'     },
  { id: 4,  color: 'purple', category_label: 'History',   stat_number: '22',    stat_label: 'World Cup winners named',      headline: 'Champions return to defend glory',                   body: '22 players who lifted the trophy at Qatar 2022 are back, hungry to become back-to-back world champions and etch their names into history forever.',                        image_key: 'wc-champions'},
  { id: 5,  color: 'sky',    category_label: 'Club Power', stat_number: '19',   stat_label: 'Man City players',             headline: 'City send a small army',                             body: 'Manchester City contribute 19 players across all nations — the highest from any single club — a testament to Pep Guardiola\'s global recruitment network.',                image_key: 'wc-mancity'  },
  { id: 6,  color: 'green',  category_label: 'Youth',     stat_number: '17',    stat_label: 'Lamine Yamal\'s age',          headline: 'Yamal arrives as the youngest star',                 body: 'At just 17, Barcelona\'s Lamine Yamal is Spain\'s brightest hope — and one of the youngest players ever to grace a World Cup stage.',                                      image_key: 'wc-mora'     },
  { id: 7,  color: 'amber',  category_label: 'Records',   stat_number: '13',    stat_label: 'Messi\'s WC goals',            headline: 'The GOAT hunts more records',                        body: 'Leo Messi arrives with 13 World Cup goals already — and with Argentina defending their title, records are there to be broken once more.',                                  image_key: 'wc-messi'    },
  { id: 8,  color: 'cyan',   category_label: 'Experience', stat_number: '6',    stat_label: 'Sixth World Cup',              headline: 'Veterans who\'ve seen it all',                       body: 'A handful of players are appearing at their sixth World Cup — an almost unimaginable feat of longevity at the very top of international football.',                        image_key: 'wc-legends'  },
  { id: 9,  color: 'purple', category_label: 'Physical',  stat_number: '2.05m', stat_label: 'Tallest player (Wiegele)',     headline: 'Giants of the game',                                 body: 'Austria\'s Patrick Wiegele stands 2.05 m tall — the tallest player at the tournament, giving set-pieces a whole new meaning for his side.',                               image_key: 'wc-wiegele'  },
  { id: 10, color: 'sky',    category_label: 'Physical',  stat_number: '1.60m', stat_label: 'Shortest player',              headline: 'Small but unstoppable',                              body: 'At 1.60 m, the smallest player in the squads proves that heart, technique and vision matter far more than centimetres when the World Cup comes around.',                   image_key: 'wc-yanis'    },
];

// ── Category colour config ────────────────────────────────────────────────────

const COLORS = {
  cyan:   { num: '#22d3ee', glow: 'rgba(34,211,238,0.25)',  badge: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
  amber:  { num: '#fbbf24', glow: 'rgba(251,191,36,0.25)',  badge: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  purple: { num: '#c084fc', glow: 'rgba(192,132,252,0.25)', badge: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  green:  { num: '#4ade80', glow: 'rgba(74,222,128,0.25)',  badge: 'bg-green-500/20 text-green-300 border-green-500/30' },
  sky:    { num: '#38bdf8', glow: 'rgba(56,189,248,0.25)',  badge: 'bg-sky-500/20 text-sky-300 border-sky-500/30' },
};
const getColor = (c) => COLORS[c] || COLORS.cyan;

// ── Background image with graceful fallback ───────────────────────────────────

function SlideBg({ imageKey }) {
  const [loaded, setLoaded] = useState(false);
  const [err, setErr]       = useState(false);

  if (!imageKey || err) return null;

  return (
    <img
      src={wcImageUrl(imageKey)}
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
      style={{
        opacity:    loaded ? 0.75 : 0,
        filter:     'saturate(0.85) brightness(1.15)',
        transition: 'opacity 1s ease',
      }}
      onLoad={() => setLoaded(true)}
      onError={() => setErr(true)}
    />
  );
}

// ── Single story slide ────────────────────────────────────────────────────────

function StorySlide({ story, index, total, active }) {
  const col     = getColor(story.color);
  const isLast  = index === total - 1;

  return (
    <section
      data-index={index}
      className="relative flex flex-col overflow-hidden"
      style={{ height: '100dvh', minHeight: '100vh', scrollSnapAlign: 'start', flexShrink: 0 }}
    >
      {/* Background image */}
      <SlideBg imageKey={story.image_key} />

      {/* Gradient overlays — heavy at bottom, light at top */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, #050b14 20%, rgba(5,11,20,0.5) 50%, rgba(5,11,20,0.05) 100%)',
        }}
      />

      {/* Slide number — top right */}
      <div className="absolute top-16 right-5 z-10">
        <span className="text-white/20 text-xs font-mono tabular-nums">
          {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
      </div>

      {/* Main content — bottom aligned */}
      <div className="relative z-10 flex flex-col justify-end flex-1 px-6 pb-10 pt-20 max-w-2xl w-full mx-auto">

        {/* Category badge */}
        <div className="mb-5">
          <span className={`text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border ${col.badge}`}>
            {story.category_label}
          </span>
        </div>

        {/* THE BIG NUMBER */}
        <div
          className="font-black leading-none mb-2 select-none"
          style={{
            fontSize:   'clamp(72px, 18vw, 130px)',
            color:       col.num,
            textShadow: `0 0 40px ${col.glow}, 0 0 80px ${col.glow}`,
            letterSpacing: '-0.03em',
          }}
        >
          {story.stat_number}
        </div>

        {/* Stat label */}
        <p
          className="text-white/50 font-semibold uppercase tracking-wider mb-4"
          style={{ fontSize: 'clamp(11px, 3vw, 14px)' }}
        >
          {story.stat_label}
        </p>

        {/* Divider */}
        <div className="w-10 h-0.5 mb-4 rounded-full" style={{ backgroundColor: col.num, opacity: 0.6 }} />

        {/* Headline */}
        <h2
          className="text-white font-bold leading-tight mb-3"
          style={{ fontSize: 'clamp(18px, 5vw, 26px)' }}
        >
          {story.headline}
        </h2>

        {/* Body text */}
        <p className="text-white/55 leading-relaxed" style={{ fontSize: 'clamp(13px, 3.5vw, 15px)' }}>
          {story.body}
        </p>

        {/* Swipe hint — only on first slide */}
        {index === 0 && (
          <div className="mt-8 flex items-center gap-2 text-white/25 text-xs select-none">
            <span>Swipe up for next</span>
            <ChevronDown size={13} className="animate-bounce" />
          </div>
        )}
      </div>
    </section>
  );
}


// ── Main page ─────────────────────────────────────────────────────────────────

export default function WC2026StatsPage() {
  const navigate        = useNavigate();
  // Pre-populate with fallback so the page renders instantly — API updates silently
  const [stories, setStories] = useState(FALLBACK_STORIES);
  const [current, setCurrent] = useState(0);
  const containerRef    = useRef(null);
  const observerRef     = useRef(null);

  // Return to WC2026 hub after 5 min of inactivity (kiosk mode).
  // resetInactivity is wired to the inner scroll container so iOS scroll events
  // (which don't bubble to window) also reset the timer.
  const resetInactivity = useKioskInactivity({ enabled: true, timeoutMs: 5 * 60 * 1000 });

  // Fetch stories — silently replaces fallback data once the API responds
  useEffect(() => {
    axios
      .get(`${API_URL}/api/football/worldcup/squad-stories`, { headers: authH() })
      .then(r => { if (r.data.stories?.length) setStories(r.data.stories); })
      .catch(() => {});
  }, []);

  // Track current slide via IntersectionObserver
  useEffect(() => {
    if (!stories.length || !containerRef.current) return;

    observerRef.current?.disconnect();

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setCurrent(parseInt(entry.target.dataset.index, 10));
          }
        });
      },
      { root: containerRef.current, threshold: 0.55 }
    );

    containerRef.current
      .querySelectorAll('[data-index]')
      .forEach(el => obs.observe(el));

    observerRef.current = obs;
    return () => obs.disconnect();
  }, [stories]);

  // Programmatic scroll to slide
  const goTo = useCallback((idx) => {
    const el = containerRef.current?.querySelector(`[data-index="${idx}"]`);
    el?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') goTo(Math.min(current + 1, stories.length - 1));
      if (e.key === 'ArrowUp'   || e.key === 'ArrowLeft')  goTo(Math.max(current - 1, 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current, stories.length, goTo]);

  return (
    <div className="fixed inset-0 bg-[#050b14]" style={{ fontFamily: 'inherit' }}>

      {/* ── Fixed top bar ── */}
      <header className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 py-3">
        {/* Back */}
        <button
          onClick={() => navigate('/wc2026')}
          className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors active:scale-95"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">WC2026</span>
        </button>

        {/* Title */}
        <div className="text-center absolute left-1/2 -translate-x-1/2">
          <p className="text-white/90 text-xs font-bold uppercase tracking-widest">Squads in Stats</p>
        </div>

        {/* Spacer */}
        <div className="w-16" />
      </header>

      {/* ── Progress bar — thin line at very top ── */}
      <div className="absolute top-0 left-0 right-0 z-40 h-0.5 bg-white/10">
        <div
          className="h-full bg-white/60 transition-all duration-500"
          style={{ width: stories.length ? `${((current + 1) / stories.length) * 100}%` : '0%' }}
        />
      </div>

      {/* ── Scroll container ── */}
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-y-scroll"
        style={{ scrollSnapType: 'y mandatory', scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch' }}
        onScroll={resetInactivity}
        onTouchStart={resetInactivity}
      >
        {stories.map((story, i) => (
          <StorySlide
            key={story.id}
            story={story}
            index={i}
            total={stories.length}
            active={i === current}
          />
        ))}

        {/* Empty state */}
        {!stories.length && (
          <div className="h-screen flex items-center justify-center text-white/30 text-sm">
            No stories available yet
          </div>
        )}
      </div>

      {/* ── Right-side progress dots ── */}
      {stories.length > 1 && (
        <nav className="absolute right-3 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-2">
          {stories.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to stat ${i + 1}`}
              className="transition-all duration-300 rounded-full"
              style={{
                width:           i === current ? 6  : 5,
                height:          i === current ? 22 : 5,
                backgroundColor: i === current ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
        </nav>
      )}
    </div>
  );
}
